// Manual overrides. A rule constrains only the dimensions it names; the most specific
// matching rule wins, so pinning Social + Facebook leaves other publishers under Social
// on their computed factor.

import { useState, useEffect, useCallback } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { fmtCurrency } from '../../format.js';
import { T } from '../../../../shared/styles/tokens.js';

const EMPTY = {
  market: '', category: '', channel_key: '', publisher_key: '', creative_format: '',
  brand: '', producer: '', scope: 'client', competitor_brand: '', ratio: '', reason: '',
};

const DIMS = [
  ['market', 'Market'], ['category', 'Category'], ['channel_key', 'Channel key'],
  ['publisher_key', 'Publisher key'], ['creative_format', 'Creative format'],
  ['brand', 'Brand'], ['producer', 'Producer'],
];

export default function OverridesSection() {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [impact, setImpact] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    cmApi.getOverrides(clientId).then(setData).catch(e => setErr(e.message));
    cmApi.getAppliedSummary(clientId)
      .then(d => setImpact(d.summary?.overrides || []))
      .catch(() => setImpact([]));
  }, [clientId]);

  useEffect(load, [load]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v !== null));
      body.ratio = Number(form.ratio);
      await cmApi.createOverride(clientId, body);
      setForm(EMPTY);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    setBusy(true); setErr(null);
    try { await cmApi.deleteOverride(clientId, id); load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!clientId) return <p className="o-state">Select a client to manage overrides.</p>;

  const overrides = data?.overrides || [];
  const history = data?.history || [];
  const impactById = Object.fromEntries(impact.map(i => [i.id, i]));

  const field = (key, label) => (
    <div className="o-ctrl">
      <label className="o-field-lbl">{label}</label>
      <input className="o-control" value={form[key]} placeholder="any"
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </div>
  );

  return (
    <div>
      <p className="o-note">
        An override replaces a <em>calculated</em> factor. It does not replace a verified
        figure — that one is your own matched actual spend, and overriding it would be
        overriding the measurement rather than the estimate drawn from it. Leave a dimension
        blank to mean "any". The most specific matching rule wins.
      </p>

      {err && <div className="o-state o-error" style={{ textAlign: 'left', padding: `${T.s2}px 0` }}>{err}</div>}

      <div className="o-panel" style={{ padding: `${T.s4}px ${T.s5}px`, marginBottom: T.s5 }}>
        <p className="o-eyebrow" style={{ marginBottom: T.s3 }}>New override</p>
        <div className="o-ctrl-row" style={{ padding: 0, border: 0, gap: T.s4 }}>
          {DIMS.map(([k, l]) => field(k, l))}
          <div className="o-ctrl">
            <label className="o-field-lbl">Scope</label>
            <select className="o-control" value={form.scope}
              onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}>
              <option value="client">All advertisers</option>
              <option value="competitor">One competitor</option>
            </select>
          </div>
          {form.scope === 'competitor' && field('competitor_brand', 'Competitor brand')}
          <div className="o-ctrl">
            <label className="o-field-lbl">Factor</label>
            <input className="o-control" type="number" step="0.01" value={form.ratio}
              onChange={e => setForm(f => ({ ...f, ratio: e.target.value }))} />
          </div>
          <div className="o-ctrl" style={{ flex: 1, minWidth: 180 }}>
            <label className="o-field-lbl">Reason</label>
            <input className="o-control" value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </div>
        </div>
        <div style={{ marginTop: T.s4 }}>
          <button className="o-btn o-btn-primary" onClick={save} disabled={busy || !form.ratio}>
            {busy ? 'Saving…' : 'Add override'}
          </button>
          <span style={{ fontSize: 11, color: T.text3, marginLeft: T.s3 }}>
            Takes effect on the next calculation.
          </span>
        </div>
      </div>

      <div className="o-panel" style={{ overflowX: 'auto', marginBottom: T.s5 }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th>Applies to</th><th>Scope</th><th>Factor</th>
              <th>Specificity</th><th>Records</th><th>Calibrated</th><th>Reason</th><th></th>
            </tr>
          </thead>
          <tbody>
            {overrides.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: T.text3 }}>
                No overrides. Every figure comes from a calculated factor.
              </td></tr>
            )}
            {overrides.map(o => {
              const applies = DIMS.map(([k]) => o[k]).filter(Boolean).join(' · ') || 'All records';
              const im = impactById[o.id];
              const orphan = im && Number(im.records) === 0;
              return (
                <tr key={o.id}>
                  <td className="o-strong">{applies}</td>
                  <td>
                    {o.scope === 'competitor'
                      ? <span className="o-badge o-grey">{o.competitor_brand}</span>
                      : <span style={{ color: T.text3 }}>all</span>}
                  </td>
                  <td className="o-num">{Number(o.ratio).toFixed(3)}×</td>
                  <td className="o-num">{o.specificity}</td>
                  <td className="o-num">
                    {im ? Number(im.records).toLocaleString() : '—'}
                    {/* A rule matching nothing is usually a mistyped dimension value
                        rather than a deliberate no-op. */}
                    {orphan && <span className="o-badge o-err" style={{ marginLeft: 6 }}>matches nothing</span>}
                  </td>
                  <td className="o-num">{im ? fmtCurrency(im.calibrated_budget) : '—'}</td>
                  <td style={{ color: T.text3 }}>{o.reason || '—'}</td>
                  <td className="o-acts">
                    <button className="o-btn o-btn-sm" disabled={busy}
                      onClick={() => remove(o.id)}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="o-eyebrow" style={{ marginBottom: T.s3 }}>Change history</p>
      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr><th>When</th><th>Action</th><th>From</th><th>To</th><th>By</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: T.text3 }}>Nothing yet.</td></tr>
            )}
            {history.map(h => (
              <tr key={h.id}>
                <td>{String(h.changed_at).slice(0, 16).replace('T', ' ')}</td>
                <td><span className="o-badge o-grey">{h.action}</span></td>
                <td className="o-num">{h.previous_ratio != null ? `${Number(h.previous_ratio).toFixed(3)}×` : '—'}</td>
                <td className="o-num">{h.ratio != null ? `${Number(h.ratio).toFixed(3)}×` : '—'}</td>
                <td style={{ color: T.text3 }}>{h.changed_by || '—'}</td>
                <td style={{ color: T.text3 }}>{h.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
